import type { SellerShopStatus } from "@/api"

// Строка списка «Магазины»: продавец (по user id), обогащённый его витриной
// каталога, плюс витрины, у которых продавца нет. Вынесено из pages/shops.tsx,
// чтобы склейку проверял тест (26.09.2026: продавец без shop_id выводился
// двумя строками).
export type ShopRow = {
  key: string
  name: string
  sellerName: string
  location: string
  productCount: number | null
  status: SellerShopStatus
  verified: boolean
  href: string
  detail: { kind: "storefront"; slug: string } | { kind: "seller"; userId: string }
  // Сегментация: продажи (оборот) и дата создания.
  sales: number
  orders: number
  createdAt: string | null
}

type SellerLike = {
  id: string
  shopId?: string | null
  shopName: string
  sellerName: string
  status: SellerShopStatus
  verified?: boolean
  createdAt?: string | null
}
type StorefrontLike = {
  id: string
  slug: string
  name: string
  location: string
  productCount: number
  status: SellerShopStatus
  verified?: boolean
}

export function mergeShopRows(
  sellers: SellerLike[],
  storefronts: StorefrontLike[],
  salesById: Map<string, { gmv: number; orders: number }>,
): ShopRow[] {
  // Join sellers to their catalog storefront; append catalog storefronts that
  // no seller owns. (React Compiler memoizes these derivations — no useMemo.)
  const sfById = new Map(storefronts.map((s) => [s.id, s]))
  const owned = new Set<string>()
  const rows: ShopRow[] = sellers.map((s) => {
    // У продавца без отдельной витрины магазином служит его собственный id
    // (usr_…): так его хранят товары, заказы и публичный список магазинов.
    // Без этого такой продавец выводился двумя строками — продавцом и
    // «ничейной» витриной (26.09.2026).
    const sf = sfById.get(s.shopId ?? s.id)
    if (sf) owned.add(sf.id)
    // Продажи считаются по shopId групп заказов: у витрины это её id, иначе
    // — id продавца (usr_), как заказы и хранят.
    const salesKey = s.shopId ?? s.id
    const sales = salesById.get(salesKey)
    return {
      key: s.id,
      name: s.shopName,
      sellerName: s.sellerName,
      location: sf?.location ?? "",
      productCount: sf?.productCount ?? null,
      status: s.status,
      // Бейдж «Проверенный»: приоритет у витрины, иначе флаг продавца.
      verified: sf?.verified ?? s.verified ?? false,
      // Sellers without a published storefront have no slug — moderate them
      // on the seller detail page; the rest open their storefront.
      href: sf ? `/shops/${sf.slug}` : `/shops/seller/${s.id}`,
      detail: sf ? { kind: "storefront", slug: sf.slug } : { kind: "seller", userId: s.id },
      sales: sales?.gmv ?? 0,
      orders: sales?.orders ?? 0,
      createdAt: s.createdAt ?? null,
    }
  })
  for (const sf of storefronts) {
    if (owned.has(sf.id)) continue
    const sales = salesById.get(sf.id)
    rows.push({
      key: `sf:${sf.id}`,
      name: sf.name,
      sellerName: "",
      location: sf.location,
      productCount: sf.productCount,
      status: sf.status,
      verified: sf.verified ?? false,
      href: `/shops/${sf.slug}`,
      detail: { kind: "storefront", slug: sf.slug },
      sales: sales?.gmv ?? 0,
      orders: sales?.orders ?? 0,
      createdAt: null,
    })
  }

  return rows
}
